/**
 * Brushless / inverter motor detection.
 *
 * Models below use brushless (direct-drive or BLDC inverter) motors and therefore
 * have NO carbon brushes — any "carbon brush" workflow steps are not applicable.
 *
 * Source: manufacturer specs for the listed series.
 *
 * Edit this list to extend coverage for additional brushless series.
 */

interface BrushlessRule {
  brand: string; // canonical brand (case-insensitive match)
  // Returns true if the given model number belongs to a brushless series for this brand.
  matches: (model: string) => boolean;
  label: string; // human-readable description (for debugging / future UI)
}

const BRUSHLESS_RULES: BrushlessRule[] = [
  // AEG ÖKO Mix / ÖKOMix 6000–9000 series — ProSteam / ÖkoInverter brushless motor.
  // Two naming schemes:
  //   - Legacy: L<series digit>...   e.g. L6FB64470, L7FE74485, L8FE74485, L9FE96695
  //   - Current: LR<series digit>... e.g. LR7386U4 (7000), LR6N6261 (6000), LR9G9484 (9000)
  {
    brand: "AEG",
    label: "AEG 6000/7000/8000/9000 series (inverter)",
    matches: (m) => {
      const s = m.trim().toUpperCase();
      return /^LR?[6-9]/.test(s);
    },
  },

  // Bosch Series 4 / 6 / 8 — EcoSilence Drive brushless motor.
  // Series encoded by the 5th character: WAN24… (S4), WAN28… also S4, WAU28… (S6), WAV28… (S8),
  // WAW32… (S8), WAX32… (S8). Practical heuristic: anything WAN/WAU/WAV/WAW/WAX is brushless.
  {
    brand: "Bosch",
    label: "Bosch Series 4/6/8 (EcoSilence Drive)",
    matches: (m) => /^WA[NUVWX]/i.test(m.trim()),
  },

  // Siemens iQ300 / iQ500 — iQdrive brushless motor.
  // iQ300 = WM14N…, iQ500 = WM14T… / WU14… ; broader: WM14[NTU] and WU14.
  {
    brand: "Siemens",
    label: "Siemens iQ300/iQ500 (iQdrive)",
    matches: (m) => /^WM14[NTU]/i.test(m.trim()) || /^WU14/i.test(m.trim()),
  },

  // Miele washers — ProfiEco / EcoSpeed / direct-drive brushless motors across modern series.
  // Series prefixes: WCA, WWD, WEA, WEG, WWR, plus the WQ 1000 WPS / WQ 1200 WPS commercial line.
  {
    brand: "Miele",
    label: "Miele WCA/WWD/WEA/WEG/WWR + WQ 1000/1200 WPS (brushless)",
    matches: (m) => {
      const s = m.trim().toUpperCase().replace(/\s+/g, "");
      if (/^(WCA|WWD|WEA|WEG|WWR)/.test(s)) return true;
      // WQ 1000 WPS / WQ 1200 WPS (spaces stripped above)
      if (/^WQ1[02]00WPS/.test(s)) return true;
      return false;
    },
  },
];

const normBrand = (b: string) => b.trim().toLowerCase();

/**
 * Returns true if the appliance has a brushless motor (no carbon brushes).
 * Returns false when brand or model is missing, or no rule matches.
 */
export function hasBrushlessMotor(
  brand: string | null | undefined,
  modelNumber: string | null | undefined,
): boolean {
  if (!brand || !modelNumber) return false;
  const b = normBrand(brand);
  const m = modelNumber.trim();
  if (!m) return false;
  return BRUSHLESS_RULES.some((r) => normBrand(r.brand) === b && r.matches(m));
}

/**
 * Returns true if a workflow item title looks like a carbon-brush step that
 * should be hidden for brushless motors.
 */
export function isCarbonBrushItem(title: string | null | undefined): boolean {
  if (!title) return false;
  return /carbon\s*brush/i.test(title);
}
